import React, { useState, useEffect, useCallback, useRef } from "react";
import { getExcelPreview } from "../services/api";
import { UploadedFileInfo } from "../types";
import "./ExcelPreview.css";

interface ExcelPreviewProps {
  file: File | null;
  showPreview: boolean;
  onPreviewStateChange?: (updates: {
    showPreview?: boolean;
    excelPreview?: {
      selected_sheet: string | null;
      available_sheets: string[];
      preview: string | null;
      error: string | null;
      loadingPreview?: boolean;
    };
  }) => void;
  excelPreview?: {
    selected_sheet: string | null;
    available_sheets: string[];
    preview: string | null;
    error: string | null;
    loadingPreview?: boolean;
  };
  autoLoadOnMount?: boolean;
  hideSheetSelector?: boolean;
  containerClassName?: string;
  s3PreviewUrl?: string | null;
  s3PreviewLoading?: boolean;
  uploadedFileInfo?: UploadedFileInfo;
}

const ExcelPreview: React.FC<ExcelPreviewProps> = ({
  file,
  showPreview,
  onPreviewStateChange,
  excelPreview,
  autoLoadOnMount = false,
  containerClassName = "",
  s3PreviewUrl,
  s3PreviewLoading = false,
  uploadedFileInfo,
}) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track which file the current preview belongs to
  const currentFileRef = useRef<string | null>(null);
  
  // Determine the display filename (from local file or S3)
  const displayFilename = file?.name || uploadedFileInfo?.filename;
  
  // Determine if we have any file source
  const hasFileSource = !!file || !!uploadedFileInfo;

  // Cleanup blob URL
  const cleanupBlobUrl = useCallback((url: string | null) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }, []);

  // Reset state when file changes
  useEffect(() => {
    const fileName = file?.name || null;
    
    // If file changed, reset the preview state
    if (currentFileRef.current !== fileName) {
      // Cleanup old blob URL
      cleanupBlobUrl(pdfBlobUrl);
      
      // Reset state
      setPdfBlobUrl(null);
      setError(null);
      setIsLoading(false);
      
      // Update the current file reference
      currentFileRef.current = fileName;
    }
  }, [file, pdfBlobUrl, cleanupBlobUrl]);

  // On mount, check if we have a cached preview from parent
  useEffect(() => {
    if (excelPreview?.preview && !pdfBlobUrl && file) {
      // Check if the cached preview matches the current file
      if (excelPreview.selected_sheet === file.name) {
        setPdfBlobUrl(excelPreview.preview);
      }
    }
  }, [excelPreview, pdfBlobUrl, file]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't cleanup here - the parent holds the reference
      // Only cleanup when file changes (handled above)
    };
  }, []);

  const loadPreview = useCallback(async () => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    // Notify parent about loading state
    if (onPreviewStateChange) {
      onPreviewStateChange({
        excelPreview: {
          selected_sheet: null,
          available_sheets: [],
          preview: null,
          error: null,
          loadingPreview: true,
        },
      });
    }

    try {
      const result = await getExcelPreview(file);

      if (result.error) {
        setError(result.error);
        setIsLoading(false);
        
        if (onPreviewStateChange) {
          onPreviewStateChange({
            excelPreview: {
              selected_sheet: null,
              available_sheets: [],
              preview: null,
              error: result.error,
              loadingPreview: false,
            },
          });
        }
        return;
      }

      if (result.pdf_data) {
        // Cleanup previous blob URL if any
        cleanupBlobUrl(pdfBlobUrl);
        
        // Convert base64 to blob and create URL
        const binaryString = atob(result.pdf_data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        setPdfBlobUrl(url);
        setIsLoading(false);

        // Notify parent about success - store file name to identify the preview
        if (onPreviewStateChange) {
          onPreviewStateChange({
            showPreview: true,
            excelPreview: {
              selected_sheet: file.name,
              available_sheets: [],
              preview: url,
              error: null,
              loadingPreview: false,
            },
          });
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load preview";
      setError(errorMessage);
      setIsLoading(false);

      if (onPreviewStateChange) {
        onPreviewStateChange({
          excelPreview: {
            selected_sheet: null,
            available_sheets: [],
            preview: null,
            error: errorMessage,
            loadingPreview: false,
          },
        });
      }
    }
  }, [file, pdfBlobUrl, onPreviewStateChange, cleanupBlobUrl]);

  // Auto-load preview when needed
  useEffect(() => {
    if (autoLoadOnMount && file && showPreview && !pdfBlobUrl && !isLoading && !error) {
      // Only load if we don't have a cached preview for this file
      const hasCachedPreview = excelPreview?.preview && excelPreview.selected_sheet === file.name;
      if (!hasCachedPreview) {
        loadPreview();
      }
    }
  }, [file, showPreview, autoLoadOnMount, pdfBlobUrl, isLoading, error, loadPreview, excelPreview]);

  // Show S3 preview if we have an S3 URL and no local file
  if (!file && s3PreviewUrl && showPreview) {
    return (
      <div className={`excel-preview-container pdf-preview ${containerClassName}`}>
        <div className="excel-preview-header">
          <h3>Preview: {displayFilename}</h3>
        </div>
        <div className="excel-preview-content pdf-iframe-container">
          <iframe
            src={s3PreviewUrl}
            title={`Preview: ${displayFilename}`}
            className="pdf-preview-iframe"
          />
        </div>
      </div>
    );
  }
  
  // Show loading state for S3 preview
  if (!file && s3PreviewLoading && hasFileSource) {
    return (
      <div className={`excel-preview-container ${containerClassName}`}>
        <div className="excel-preview-header">
          <h3>Preview: {displayFilename}</h3>
        </div>
        <div className="excel-preview-content">
          <div className="preview-loading">
            <div className="loading-spinner"></div>
            <span>Loading preview from storage...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!showPreview || !hasFileSource) {
    return (
      <div className={`excel-preview-placeholder ${containerClassName}`}>
        <div className="placeholder-content">
          <span className="placeholder-icon">📊</span>
          <p>Excel preview will appear here</p>
          <p className="placeholder-hint">
            {hasFileSource ? "Preview is ready" : "Select an Excel file to preview"}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`excel-preview-container ${containerClassName}`}>
        <div className="excel-preview-header">
          <h3>Preview: {displayFilename}</h3>
        </div>
        <div className="excel-preview-content">
          <div className="preview-error">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={() => loadPreview()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`excel-preview-container ${containerClassName}`}>
        <div className="excel-preview-header">
          <h3>Preview: {displayFilename}</h3>
        </div>
        <div className="excel-preview-content">
          <div className="preview-loading">
            <div className="loading-spinner"></div>
            <span>Converting Excel to PDF...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!pdfBlobUrl) {
    // Trigger load if not already loaded
    if (!isLoading && !error) {
      loadPreview();
    }
    return (
      <div className={`excel-preview-placeholder ${containerClassName}`}>
        <div className="placeholder-content">
          <span className="placeholder-icon">📊</span>
          <p>Loading Excel preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`excel-preview-container pdf-preview ${containerClassName}`}>
      <div className="excel-preview-header">
        <h3>Preview: {displayFilename}</h3>
      </div>
      <div className="excel-preview-content pdf-iframe-container">
        <iframe
          src={pdfBlobUrl}
          title={`Preview: ${displayFilename}`}
          className="pdf-preview-iframe"
        />
      </div>
    </div>
  );
};

export default ExcelPreview;
